/**
 * Client-side session helpers.
 *
 * The JWT lives in localStorage. We verify the signature server-side via
 * /license/me + /license/refresh; in the browser we only decode the payload
 * to read `exp` and the user fields. That's safe because the JWT is only
 * used as a credential, not as ground truth.
 */

import type { Session, SessionPayload } from "@agencylens/shared";

const STORAGE_KEY = "agencylens:session";

function decodeJwt<T>(jwt: string): T {
  const parts = jwt.split(".");
  if (parts.length !== 3) throw new Error("malformed_jwt");
  let body = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
  while (body.length % 4) body += "=";
  // eslint-disable-next-line no-undef
  return JSON.parse(atob(body)) as T;
}

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

export function getSession(): Session | null {
  const jwt = localStorage.getItem(STORAGE_KEY);
  if (!jwt) return null;
  try {
    const payload = decodeJwt<SessionPayload>(jwt);
    if (typeof payload.exp !== "number" || payload.exp < nowSec()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { jwt, payload };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function setSession(jwt: string): Session | null {
  localStorage.setItem(STORAGE_KEY, jwt);
  return getSession();
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** Seconds until the session expires, or 0 if no session / already expired. */
export function sessionSecondsLeft(): number {
  const s = getSession();
  if (!s) return 0;
  return Math.max(0, s.payload.exp - nowSec());
}

/** Returns true if the session expires within the given number of seconds. */
export function sessionExpiresSoon(within = 60 * 60 * 24 * 3): boolean {
  const left = sessionSecondsLeft();
  return left > 0 && left < within;
}
